/**
 * VISTOERA_FLAT_IMAGE_OPTIMIZATION_V1 — 平面图像优化 API client.
 *
 * Mirrors `app/api/routes/flat_image.py` route-for-route. An entirely
 * independent workflow from `../api/client`'s 物品标准图 AssetChain calls
 * (spec §五十九 isolation) even though both ultimately read/write the same
 * `asset_chains` table server-side — this module never touches
 * `/images/...`, only `/flat-images/...`.
 */
import { saasApi } from './api'

export type FlatBoundaryType = 'auto' | 'quad' | 'circle' | 'ellipse' | 'freeform' | 'unknown'
export type FlatConfidence = 'high' | 'medium' | 'low'
export type FlatTemplateKey = 'true_reproduction' | 'auto_enhance' | 'document_scan' | 'custom'
export type FlatChainStatus = 'pending' | 'processing' | 'waiting_next' | 'done' | 'failed' | 'needs_review'

export interface FlatPoint {
  x: number
  y: number
}

/** Discriminated by whatever `boundary_type` is currently set — callers
 * should only read the field(s) that make sense for that type. */
export interface FlatBoundaryGeometry {
  // quad
  top_left?: FlatPoint
  top_right?: FlatPoint
  bottom_right?: FlatPoint
  bottom_left?: FlatPoint
  // circle
  cx?: number
  cy?: number
  r?: number
  // ellipse — `angle` (not "rotation") matches `flat_image_ops.py`'s
  // geometry dict keys exactly.
  rx?: number
  ry?: number
  angle?: number
  // freeform
  points?: FlatPoint[]
}

/** Mirrors `flat_image_ops.py`'s `_ENHANCE_KEYS` exactly — `exposure`
 * (not "brightness"), all -100..100 except `sharpness` (0..100). */
export interface FlatEnhanceParams {
  exposure: number
  contrast: number
  highlights: number
  shadows: number
  temperature: number
  saturation: number
  sharpness: number
}

export interface FlatCropParams {
  aspect: string
  angle: number
  flip_h: boolean
  flip_v: boolean
}

export interface FlatTemplateOut {
  key: string
  id?: string
  label: string
  is_custom: boolean
  defaults?: FlatEnhanceParams
  crop?: FlatCropParams | null
  enhance?: FlatEnhanceParams | null
}

export interface FlatChainOut {
  id: string
  workspace_id: string
  original_filename: string
  original_width: number | null
  original_height: number | null
  original_missing: boolean
  original_size_bytes: number | null
  original_url: string | null
  thumbnail_url: string | null
  flat_rectified_url: string | null
  flat_enhanced_url: string | null
  flat_boundary_type: FlatBoundaryType | null
  flat_boundary_type_label: string | null
  flat_boundary_geometry: FlatBoundaryGeometry | null
  flat_boundary_confidence: FlatConfidence | null
  flat_boundary_confidence_label: string | null
  flat_crop: FlatCropParams | null
  flat_enhance: FlatEnhanceParams
  flat_template_key: FlatTemplateKey | null
  flat_custom_template_id: string | null
  flat_rectified_status: string
  flat_enhanced_status: string
  flat_needs_review: boolean
  status: FlatChainStatus
  status_label: string
  current_stage: string | null
  current_progress: number | null
  current_message: string | null
  last_error_stage: string | null
  last_error_message: string | null
  created_at: string | null
  updated_at: string | null
}

export interface FlatChainListSummary {
  total: number
  pending: number
  processing: number
  waiting_next: number
  done: number
  failed: number
  needs_review: number
}

export interface FlatChainListResponse {
  chains: FlatChainOut[]
  summary: FlatChainListSummary
  page: number
  page_size: number
  total_pages: number
}

const base = (workspaceId: string) => `/workspaces/${workspaceId}/flat-images`

export const listFlatTemplates = (workspaceId: string) =>
  saasApi.get<{ templates: FlatTemplateOut[] }>(`${base(workspaceId)}/templates`)

export const createFlatCustomTemplate = (
  workspaceId: string,
  data: { name: string; crop?: FlatCropParams | null; enhance?: FlatEnhanceParams | null },
) => saasApi.post<{ id: string; name: string; crop: FlatCropParams; enhance: FlatEnhanceParams }>(
  `${base(workspaceId)}/templates`,
  data,
)

export const deleteFlatCustomTemplate = (workspaceId: string, templateId: string) =>
  saasApi.delete(`${base(workspaceId)}/templates/${templateId}`)

export const listFlatChains = (
  workspaceId: string,
  params?: { q?: string; status?: string; template_key?: string; page?: number; page_size?: number },
) => saasApi.get<FlatChainListResponse>(`${base(workspaceId)}/chains`, { params })

export const getFlatChain = (workspaceId: string, chainId: string) =>
  saasApi.get<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}`)

export const deleteFlatChain = (workspaceId: string, chainId: string) =>
  saasApi.delete(`${base(workspaceId)}/chains/${chainId}`)

export const detectFlatBoundary = (workspaceId: string, chainId: string) =>
  saasApi.post<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}/detect-boundary`)

export const saveFlatBoundary = (
  workspaceId: string,
  chainId: string,
  data: { boundary_type: FlatBoundaryType; geometry: FlatBoundaryGeometry },
) => saasApi.post<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}/boundary`, data)

export const saveFlatCrop = (workspaceId: string, chainId: string, data: FlatCropParams) =>
  saasApi.post<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}/crop`, data)

export const saveFlatTemplate = (
  workspaceId: string,
  chainId: string,
  data: { template_key: FlatTemplateKey; enhance?: FlatEnhanceParams | null; custom_template_id?: string | null },
) => saasApi.post<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}/template`, data)

export interface FlatPreviewOverride {
  boundary_type?: FlatBoundaryType | null
  geometry?: FlatBoundaryGeometry | null
  crop?: FlatCropParams | null
  enhance?: FlatEnhanceParams | null
}

export const previewFlatChain = (workspaceId: string, chainId: string, body: FlatPreviewOverride) =>
  saasApi.post<{ preview_data_url: string }>(`${base(workspaceId)}/chains/${chainId}/preview`, body)

export const saveFlatChain = (workspaceId: string, chainId: string, body: FlatPreviewOverride) =>
  saasApi.post<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}/save`, body)

export const retryFlatChain = (workspaceId: string, chainId: string) =>
  saasApi.post<FlatChainOut>(`${base(workspaceId)}/chains/${chainId}/retry`)

export const batchStartFlatChains = (workspaceId: string, chainIds: string[], templateKey?: string | null) =>
  saasApi.post<{ ok: boolean; count: number }>(`${base(workspaceId)}/chains/batch-start`, {
    chain_ids: chainIds,
    template_key: templateKey ?? undefined,
  })

export interface FlatJobOut {
  id: string
  job_type: string
  related_asset_id: string | null
  status: string
  progress: number | null
  stage: string | null
  message: string | null
  error_code: string | null
  error_detail: string | null
}

export const getFlatJob = (workspaceId: string, jobId: string) =>
  saasApi.get<FlatJobOut>(`${base(workspaceId)}/jobs/${jobId}`)

export const DEFAULT_FLAT_ENHANCE: FlatEnhanceParams = {
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  temperature: 0,
  saturation: 0,
  sharpness: 0,
}

export const FLAT_ENHANCE_SLIDERS: Array<{ key: keyof FlatEnhanceParams; label: string; min: number; max: number }> = [
  { key: 'exposure', label: '曝光', min: -100, max: 100 },
  { key: 'contrast', label: '对比度', min: -100, max: 100 },
  { key: 'highlights', label: '高光', min: -100, max: 100 },
  { key: 'shadows', label: '阴影', min: -100, max: 100 },
  { key: 'temperature', label: '色温（冷→暖）', min: -100, max: 100 },
  { key: 'saturation', label: '饱和度', min: -100, max: 100 },
  { key: 'sharpness', label: '锐度', min: 0, max: 100 },
]

export const FLAT_CROP_ASPECTS: Array<{ value: string; label: string }> = [
  { value: 'free', label: '自由' },
  { value: 'original', label: '原始比例' },
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
  { value: 'circle', label: '圆形' },
  { value: 'ellipse', label: '椭圆' },
]

export const FLAT_BOUNDARY_TYPE_OPTIONS: Array<{ value: FlatBoundaryType; label: string }> = [
  { value: 'auto', label: '自动识别' },
  { value: 'quad', label: '四点透视' },
  { value: 'freeform', label: '自由边界' },
  { value: 'circle', label: '圆形' },
  { value: 'ellipse', label: '椭圆形' },
]
